import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, TrendingUp } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
interface StoreDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  store: any;
}
const data = [{
  name: 'Jan',
  score: 4.1
}, {
  name: 'Feb',
  score: 4.2
}, {
  name: 'Mär',
  score: 4.0
}, {
  name: 'Apr',
  score: 4.3
}, {
  name: 'Mai',
  score: 4.5
}, {
  name: 'Jun',
  score: 4.6
}];
export function StoreDetailPanel({
  isOpen,
  onClose,
  store
}: StoreDetailPanelProps) {
  return <AnimatePresence data-id="element-80">
      {isOpen && <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="absolute right-0 top-0 bottom-0 w-[400px] bg-white shadow-2xl z-[500] border-l border-gray-200 flex flex-col" data-id="element-81">
          {/* Header */}
          <div className="relative h-48 bg-gray-200" data-id="element-82">
            <img src="https://images.unsplash.com/photo-1574258495973-f7977603b6d2?auto=format&fit=crop&w=1000&q=80" alt="Filiale Außenansicht" className="w-full h-full object-cover" data-id="element-83" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6" data-id="element-84">
              <Badge variant="warning" className="self-start mb-2" data-id="element-85">
                {store?.type || 'Fielmann Filiale'}
              </Badge>
              <h2 className="text-2xl font-bold text-white leading-tight" data-id="element-86">
                {store?.name || 'Fielmann Hamburg-Hafencity'}
              </h2>
              <p className="text-white/80 text-sm flex items-center gap-1" data-id="element-87">
                <MapPinIcon className="w-3 h-3" data-id="element-88" />{' '}
                {store?.address || 'Hamburg, Hafencity'}
              </p>
            </div>
            <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 text-white rounded-full transition-colors backdrop-blur-sm" data-id="element-89">
              <X size={20} data-id="element-90" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-91">
            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-4" data-id="element-92">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-93">
                <div className="text-sm text-gray-500 mb-1" data-id="element-94">
                  Google Bewertung
                </div>
                <div className="flex items-baseline gap-2" data-id="element-95">
                  <span className="text-3xl font-bold text-gray-900" data-id="element-96">
                    {store?.score || '4.6'}
                  </span>
                  <div className="flex text-yellow-400" data-id="element-97">
                    <Star size={14} fill="currentColor" data-id="element-98" />
                    <Star size={14} fill="currentColor" data-id="element-99" />
                    <Star size={14} fill="currentColor" data-id="element-100" />
                    <Star size={14} fill="currentColor" data-id="element-101" />
                    <Star size={14} fill="currentColor" className="text-gray-300" data-id="element-102" />
                  </div>
                </div>
                <div className="text-xs text-green-600 font-medium mt-1 flex items-center" data-id="element-103">
                  <TrendingUp size={12} className="mr-1" data-id="element-104" /> +0.2 vs. Vormonat
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-105">
                <div className="text-sm text-gray-500 mb-1" data-id="element-106">
                  Bewertungen gesamt
                </div>
                <div className="flex items-baseline gap-2" data-id="element-107">
                  <span className="text-3xl font-bold text-gray-900" data-id="element-108">428</span>
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-109">
                  8 neue diese Woche
                </div>
              </div>
            </div>

            {/* Trend Chart */}
            <div data-id="element-110">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center justify-between" data-id="element-111">
                Bewertungstrend (6 Monate)
                <span className="text-xs font-normal text-gray-500" data-id="element-112">
                  Live-Daten
                </span>
              </h3>
              <div className="h-40 w-full" data-id="element-113">
                <ResponsiveContainer width="100%" height="100%" data-id="element-114">
                  <AreaChart data={data} data-id="element-115">
                    <defs data-id="element-116">
                      <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1" data-id="element-117">
                        <stop offset="5%" stopColor="#1a1a2e" stopOpacity={0.1} data-id="element-118" />
                        <stop offset="95%" stopColor="#1a1a2e" stopOpacity={0} data-id="element-119" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-120" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#9ca3af'
                }} data-id="element-121" />
                    <YAxis domain={[3.5, 5]} hide data-id="element-122" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} data-id="element-123" />
                    <Area type="monotone" dataKey="score" stroke="#1a1a2e" strokeWidth={2} fillOpacity={1} fill="url(#colorScore)" data-id="element-124" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Sentiment Analysis */}
            <div data-id="element-125">
              <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-126">
                Kundenstimmung
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-127">
                <Badge variant="success" className="px-3 py-1 text-sm" data-id="element-128">
                  Glasqualität +15%
                </Badge>
                <Badge variant="success" className="px-3 py-1 text-sm" data-id="element-129">
                  Brillenauswahl +10%
                </Badge>
                <Badge variant="danger" className="px-3 py-1 text-sm" data-id="element-130">
                  Wartezeit -8%
                </Badge>
                <Badge variant="warning" className="px-3 py-1 text-sm" data-id="element-131">
                  Erreichbarkeit -3%
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-id="element-132">
                Kunden loben häufig die{' '}
                <span className="font-medium text-gray-900" data-id="element-133">
                  Beratungsqualität
                </span>{' '}
                und die große Brillenauswahl, aber aktuelle Bewertungen erwähnen
                längere Wartezeiten für Sehtests während der Stoßzeiten
                (16:00-18:00 Uhr).
              </p>
            </div>

            {/* Recent Review Snippet */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-134">
              <div className="flex items-center justify-between mb-2" data-id="element-135">
                <div className="flex items-center gap-2" data-id="element-136">
                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-xs font-bold text-blue-800" data-id="element-137">
                    M
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-138">
                    Michael S.
                  </span>
                </div>
                <span className="text-xs text-gray-500" data-id="element-139">vor 2 Tagen</span>
              </div>
              <p className="text-sm text-gray-700 italic" data-id="element-140">
                "Tolle Brillenauswahl und kompetente Beratung, aber die
                Wartezeit für den Sehtest war etwas lang..."
              </p>
              <div className="mt-3 flex justify-end" data-id="element-141">
                <Button size="sm" variant="secondary" className="text-xs h-7" data-id="element-142">
                  Mit KI antworten
                </Button>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-gray-200 bg-gray-50" data-id="element-143">
            <Button className="w-full" data-id="element-144">Vollständigen Bericht anzeigen</Button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
function MapPinIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-145">
      <path d="M20 10c0 6-9 13-9 13s-9-7-9-13a9 9 0 0 1 18 0z" data-id="element-146" />
      <circle cx="12" cy="10" r="3" data-id="element-147" />
    </svg>;
}